import { BinaryFetcherName } from '../../../../shared/fetchers';
import Locator from '../../../../shared/Locator';
import { useParams } from '../../../contexts/RouterContext';
import { useIsSecurityEnabled } from '../../../helpers/features';
import useReleaseGroupAllProjects from '../../../hooks/api/releaseGroups/useReleaseGroupAllProjects';
import { isReleaseGroupScope } from '../types';
import type { ReportsV3Params } from '../types';

const isBinaryProject = (locator: string): boolean =>
  Locator.parse(locator).fetcher === BinaryFetcherName;

/**
 * Mini hook memoizing whether the Remediation Guidance report should be shown based on
 * the security feature flag and the project(s) fetcher type(s).
 *
 * We should show the remediation guidance report if:
 * - Security is enabled
 * - AND
 * - The project is not a binary project (project scope)
 * - OR
 * - Not all of the release group's projects are bin
 */
export const useIsRemediationEnabled = (): boolean => {
  const isSecurityEnabled = useIsSecurityEnabled();
  const params = useParams<ReportsV3Params>();
  const isReleaseGroup = isReleaseGroupScope(params);

  // This hook (fetching all project locators) is used for both project and release group scope;
  // but we cannot call useReleaseGroupAllProjects conditionally (Rules of Hooks),
  // so we only call it if we are on a release group scope.
  const allReleaseGroupProjectLocators = useReleaseGroupAllProjects(
    isReleaseGroup ? params.projectGroupId : 0,
    isReleaseGroup,
  );

  // We don't show remediation guidance if security is not enabled.
  if (!isSecurityEnabled) {
    return false;
  }

  // Check that every project in the release group is a binary project.
  if (isReleaseGroup) {
    return !(allReleaseGroupProjectLocators.data?.every(isBinaryProject) ?? false);
  }

  // Handle project scope.
  return !isBinaryProject(params.project_id);
};
